import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import emailValidation from "../../../utils/taskDetail/emailValidation";

import { toastState } from "../../../recoil/toast/toastState";
import { taskPopupState } from "../../../recoil/taskDetail/taskPopupState";
import { taskDetailHeightState } from "../../../recoil/taskDetail/taskDetailHeightState";

import PropertyTitle from "../Common/PropertyTitle";

import styles from "./GuestModal.module.css";
import { AddButton } from "../Link/AddButton";
import { AttendResponse } from "../../../types/block/enum";
import { useGoogleContactsQueryOptions } from "../../../react-query/contact/queries";
import { useQuery } from "@tanstack/react-query";
import PersonIcon from "../../../compoenets/atom/icons/PersonIcon";
import { ToastKind } from "../../../types/toast";
import { useUserProfileQuery } from "../../../react-query/user/queries";
import { useFetchUserCalendarsQuery } from "../../../react-query/userCalendars/queries";

import SelectedGuestList from "./SelectedGuestList";
import EmptyButton from "./EmptyButton";

import SelectedGuestCounts from "./SelectedGuestCounts";

import { getIsGooglePrimaryCalendar } from "../../../services/block/block.service";
import GuestInputWithDropdown from "./GuestInputWithDropdown";
import { AttendeeType } from "../../../types/block/type";
import { UserCalendarType } from "../../../types/userCalendars/type";

interface GuestModalProps {
  onClose: () => void;
  setIsBuffMode: (isBuffMode: boolean) => void;
  expand: boolean;
  guestDropdownRef: React.RefObject<HTMLUListElement>;
  mutateMeetingCode: (email: string) => void;
}

export interface PositionType {
  top: string;
  left: string;
  width: string;
}

export default function GuestModal({
  onClose,
  setIsBuffMode,
  expand,
  guestDropdownRef,
  mutateMeetingCode,
}: GuestModalProps) {
  // Recoil 상태
  const setToast = useSetRecoilState(toastState);
  const [taskDetail, setTaskDetail] = useRecoilState(taskPopupState);
  const { data: userProfile } = useUserProfileQuery();
  const setTaskDetailModalHeight = useSetRecoilState(taskDetailHeightState);

  // useState를 사용한 상태
  const [contactData, setContactData] = useState([]);
  const [filteredGuests, setFilteredGuests] = useState([]);
  const [isShowGuestsInput, setIsShowGuestsInput] = useState(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const [currentActiveIndex, setCurrentActiveIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollTimeout, setScrollTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const initialGuest = useRef<AttendeeType[]>([]);

  const fetchUserCalendarsQuery = useFetchUserCalendarsQuery();
  const googleContactQuery = useQuery(useGoogleContactsQueryOptions());
  const inputRef = useRef<HTMLInputElement>(null);

  const data = taskDetail.data;
  const isSelfTask = data?.creator === userProfile?.email;

  const primaryCalendar = useMemo(() => {
    return fetchUserCalendarsQuery.data?.find((calendar) => calendar.primary);
  }, [fetchUserCalendarsQuery.data, taskDetail.data?.calendarId]);

  useEffect(() => {
    getContact();
  }, []);

  useEffect(() => {
    if (isShowGuestsInput) {
      inputRef.current?.focus();
    }
  }, [isShowGuestsInput]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      event.stopPropagation();
      if (guestDropdownRef.current && !guestDropdownRef.current.contains(event.target as Node)) {
        setFilteredGuests([]);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [guestDropdownRef]);

  useEffect(() => {
    setCurrentActiveIndex(0);
  }, [filteredGuests]);

  // TaskDetailModal의 높이를 동적으로 조절하기 위한 useEffect
  useEffect(() => {
    setTaskDetailModalHeight((prev) => prev + 30);
  }, [isShowGuestsInput]);

  useEffect(() => {
    initialGuest.current = data?.attendees ?? [];
  }, []);

  useEffect(() => {
    if (
      taskDetail?.data &&
      taskDetail.data.attendees &&
      taskDetail.data.attendees.length > 0 &&
      (!initialGuest.current || initialGuest.current?.length === 0) &&
      !taskDetail.data?.meetingCode &&
      !taskDetail.data?.hangoutLink
    ) {
      mutateMeetingCode(data?.creator ?? "");
    }
  }, [taskDetail.data?.attendees]);

  const addNewGuestToAttendees = (
    currentAttendees: AttendeeType[],
    calendar: UserCalendarType,
    newEmail: string
  ) => {
    const isGooglePrimary = getIsGooglePrimaryCalendar({ calendar });
    const calendarOwnerEmail = calendar?.gcalId;

    const updated = [...currentAttendees];

    const isFirstAddition = updated.length === 0;

    // 첫 게스트 추가이고 Google Primary라면 주인도 같이 추가
    if (isFirstAddition && isGooglePrimary && calendarOwnerEmail) {
      updated.push({
        email: calendarOwnerEmail,
        organizer: true,
        self: true,
        responseStatus: AttendResponse.ACCEPTED,
      });
    }

    const isDuplicate = updated.some((att) => att.email === newEmail);
    if (!isDuplicate) {
      updated.push({
        email: newEmail,
        responseStatus: AttendResponse.NEEDS_ACTION,
      });
    }

    return updated;
  };

  const getContact = async () => {
    setContactData(googleContactQuery.data);
    setFilteredGuests(googleContactQuery.data);
  };

  const showErrorToast = () => {
    setToast({
      isVisible: true,
      message: "The email address is incorrect",
      type: ToastKind.ERROR,
      triggerId: 0,
    });
  };

  const handleGuestInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsInvalidEmail(false);

    if (e.target.value === "") {
      setFilteredGuests([]);
      return;
    }
    if (contactData?.length === 0) return;
    setFilteredGuests(
      googleContactQuery.data?.filter((guest: AttendeeType) => guest.email.includes(e.target.value))
    );
  };

  const handleGuestInputBlur = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setIsInvalidEmail(false);
    setIsShowGuestsInput(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "Enter":
        // NOTE 기획: e.target.value 가 없고 cmd(or ctrl)+enter 때에는 바로 onSave로 taskDetail 내용 저장되도록 이벤트 버블링 유도
        // TODO 추후 onSave Ref 관리와 함께 수정 필요

        const target = e.target as HTMLInputElement;
        if (!target.value && (e.metaKey || e.ctrlKey)) {
          break;
        }

        // NOTE 나머지 경우에 한해 이벤트 버블링 차단
        e.stopPropagation();

        // NOTE e.target.value 가 없을 때는 게스트 추가되지 않도록 설정
        if (!target.value) {
          return;
        }

        if (filteredGuests[currentActiveIndex]) {
          const selectedGuest = filteredGuests[currentActiveIndex] as AttendeeType;
          return handleAddGuest(selectedGuest.email ?? "");
        }

        handleAddGuest(target.value);
        break;
      case "Escape":
        e.stopPropagation();
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        setIsShowGuestsInput(false);

        onClose();
        break;
      case "ArrowDown":
        e.stopPropagation();
        setCurrentActiveIndex(0);
        if (currentActiveIndex >= filteredGuests?.length - 1) {
          return setCurrentActiveIndex(0);
        }
        setCurrentActiveIndex(currentActiveIndex + 1);
        break;
      case "ArrowUp":
        e.stopPropagation();
        if (currentActiveIndex <= 0) {
          return setCurrentActiveIndex(filteredGuests.length - 1);
        }
        setCurrentActiveIndex(currentActiveIndex - 1);
        break;

      default:
        break;
    }
  };

  const getCurrentCalendar = useCallback(
    (calendarId: number) => {
      return (
        fetchUserCalendarsQuery?.data?.find((calendar) => calendar.id === calendarId) ??
        primaryCalendar
      );
    },
    [fetchUserCalendarsQuery.data, primaryCalendar]
  );

  const handleAddGuest = (email: string) => {
    const isValid = emailValidation(email);
    if (!isValid) {
      setIsInvalidEmail(true);
      showErrorToast();
      return;
    }

    setTaskDetail((prev) => {
      if (!prev.data) return prev;

      const currentCalendar = getCurrentCalendar(prev.data?.calendarId ?? 0);
      const updatedAttendees = addNewGuestToAttendees(
        prev.data?.attendees ?? [],
        currentCalendar ?? ({} as UserCalendarType),
        email
      );

      return {
        ...prev,
        data: {
          ...prev.data,
          attendees: updatedAttendees,
        },
      };
    });

    onClose();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setIsShowGuestsInput(true);
    setIsBuffMode(false);
  };

  const handleDelGuest = (email: string) => {
    const updatedAttendees = (data?.attendees || []).filter((guest) => guest.email !== email);

    // 모든 게스트가 삭제된 경우 early return
    if (updatedAttendees.length === 0) {
      setTaskDetail((prevState) => {
        if (!prevState.data) return prevState;
        return {
          ...prevState,
          data: {
            ...prevState.data,
            attendees: [],
          },
        };
      });
      setIsBuffMode(true);
      return;
    }

    // 일부 게스트만 삭제된 경우
    setTaskDetail((prevState) => {
      if (!prevState.data) return prevState;
      return {
        ...prevState,
        data: {
          ...prevState.data,
          attendees: updatedAttendees,
        },
      };
    });
  };

  const getGuestDropdownPosition = useMemo((): PositionType | null => {
    if (!inputRef.current) return null;
    if (filteredGuests?.length === 0) return null;

    const element = inputRef.current;
    const rect = element.getBoundingClientRect();
    const GUEST_ROW_HEIGHT = 36;

    // 드롭다운의 예상 높이 (대략적인 값, 실제로는 아이템 개수에 따라 달라짐)
    const dropdownHeight = Math.min(filteredGuests.length * GUEST_ROW_HEIGHT, 275); // 최대 275px로 제한
    const margin = 5; // 5px에서 15px로 증가

    // 화면 하단 여유 공간 확인
    const spaceBelow = window.innerHeight - rect.bottom - window.scrollY;
    const spaceAbove = rect.top + window.scrollY;

    // 위쪽에 표시해야 하는 경우
    const shouldShowAbove =
      spaceBelow < dropdownHeight + margin && spaceAbove > dropdownHeight + margin;

    if (shouldShowAbove) {
      // 위쪽에 표시
      return {
        top: `${rect.top - dropdownHeight - margin + window.scrollY}px`,
        left: `${rect.left + window.scrollX}px`,
        width: `${rect.width}px`,
      };
    }

    // 아래쪽에 표시 (기본값)
    return {
      top: `${rect.bottom + margin + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
    };
  }, [filteredGuests?.length, isShowGuestsInput, inputRef.current]);

  const handleScroll = useCallback(() => {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }

    setIsScrolling(true);
    const timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 2000);

    setScrollTimeout(timeout);
  }, [scrollTimeout]);

  useEffect(() => {
    const element = document.querySelector(`.${styles["guests-modal"]}`);
    if (element) {
      element.addEventListener("scroll", handleScroll);
      return () => {
        element.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  const renderGuestContent = () => {
    const hasGuests = data?.attendees && data?.attendees?.length > 0;

    // 빈 상태 - 게스트도 없고 입력 모드도 아닐 때
    if (!hasGuests && !isShowGuestsInput) {
      return (
        <EmptyButton
          title="Add guests"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            setIsShowGuestsInput(true);
          }}
        />
      );
    }

    return (
      <>
        {/* 기존 게스트 목록 - 게스트가 있으면 항상 표시 */}
        {hasGuests && (
          <>
            <div className={styles["guests-info__container"]}>
              <SelectedGuestCounts selectedGuestsCount={data?.attendees?.length ?? 0} />
              {!isShowGuestsInput && (
                <AddButton
                  openMore={isShowGuestsInput}
                  onClick={() => {
                    setIsShowGuestsInput(true);
                  }}
                />
              )}
            </div>
            <SelectedGuestList
              selectedGuests={data?.attendees ?? []}
              expand={expand}
              handleDelGuest={handleDelGuest}
              isSelfTask={isSelfTask}
              contactData={contactData}
            />
          </>
        )}

        {/* 게스트 입력 영역 - 입력 모드일 때만 표시 */}
        {isShowGuestsInput && (
          <GuestInputWithDropdown
            inputRef={inputRef}
            isInvalidEmail={isInvalidEmail}
            handleGuestInputChange={handleGuestInputChange}
            handleGuestInputBlur={handleGuestInputBlur}
            handleInputKeyDown={handleInputKeyDown}
            filteredGuests={filteredGuests}
            guestDropdownRef={guestDropdownRef}
            isScrolling={isScrolling}
            currentActiveIndex={currentActiveIndex}
            handleAddGuest={handleAddGuest}
            guestDropdownPosition={getGuestDropdownPosition}
            attendees={data?.attendees ?? []}
          />
        )}
      </>
    );
  };

  return (
    <div className={styles["setting-property"]}>
      <div className={styles["setting-property__btn"]}>
        <PropertyTitle
          Icon={<PersonIcon />}
          label="Guests"
          expand={expand}
        />
        <div className={styles["setting-property__guests-contents"]}>{renderGuestContent()}</div>
      </div>
    </div>
  );
}
